/**
 * /admin/ops/backup — Backup & Restore (SCRUM-314/323).
 *
 * Fuente de verdad real: Neon branches `backup-fm-*` (no manifests locales,
 * que no persisten en el runtime serverless — ver src/lib/backup-ops.ts).
 * Restore de datos NO está automatizado hoy: "Generar plan de restore"
 * devuelve los mismos pasos manuales que restore-db-backup.mjs, sin mover
 * datos — no existe un botón que ejecute un restore real.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert, ShieldQuestion, ChevronDown, Download } from "lucide-react";
import { OpsSubnav } from "@/components/OpsSubnav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";

interface NeonBranch {
  id: string;
  name: string;
  created_at: string;
  current_state?: string;
}

interface ProtectionStatus {
  status: "protected" | "degraded" | "at_risk" | "unknown" | "not_configured";
  configured: boolean;
  lastBackupAt: string | null;
  lastBackupAgeHours: number | null;
  totalBackups: number;
  verifiedCount: number;
  failedCount: number;
  r2Configured: boolean;
  error: string | null;
}

interface OperationRow {
  id: string;
  type: string;
  createdAt: string;
  actor: string | null;
  ok: boolean | null;
  detail: Record<string, unknown>;
}

const STATUS_META: Record<
  ProtectionStatus["status"],
  { label: string; className: string; icon: typeof ShieldCheck }
> = {
  protected: { label: "Protegido", className: "bg-green-500/10 text-green-700 dark:text-green-400", icon: ShieldCheck },
  degraded: { label: "Degradado", className: "bg-amber-500/10 text-amber-700 dark:text-amber-400", icon: ShieldAlert },
  at_risk: { label: "En riesgo", className: "bg-red-500/10 text-red-700 dark:text-red-400", icon: ShieldAlert },
  unknown: { label: "Desconocido", className: "bg-muted text-muted-foreground", icon: ShieldQuestion },
  not_configured: { label: "No configurado", className: "bg-muted text-muted-foreground", icon: ShieldQuestion },
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "medium" });
}

function ageLabel(h: number | null) {
  if (h == null) return "—";
  if (h < 1) return `hace ${Math.round(h * 60)}min`;
  if (h < 24) return `hace ${h.toFixed(0)}h`;
  return `hace ${(h / 24).toFixed(0)} días`;
}

export const Route = createFileRoute("/admin/ops/backup")({
  component: BackupPage,
  head: () => ({ meta: [{ title: "Backup & Restore — Structura Market" }] }),
});

function BackupPage() {
  const dialogs = useAppDialogs();
  const [status, setStatus] = useState<ProtectionStatus | null>(null);
  const [branches, setBranches] = useState<NeonBranch[]>([]);
  const [operations, setOperations] = useState<OperationRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const [restorePlan, setRestorePlan] = useState<Record<string, unknown> | null>(null);
  const [exportTenant, setExportTenant] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, h] = await Promise.all([
        apiGetJson<ProtectionStatus>("/api/admin/ops/backups?resource=status"),
        apiGetJson<{ branches: NeonBranch[] }>("/api/admin/ops/backups?resource=branches"),
        apiGetJson<{ operations: OperationRow[]; nextCursor: string | null }>(
          "/api/admin/ops/backups?resource=history",
        ),
      ]);
      setStatus(s);
      setBranches(b.branches ?? []);
      setOperations(h.operations ?? []);
      setNextCursor(h.nextCursor);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo cargar el estado de backups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMoreHistory() {
    if (!nextCursor) return;
    try {
      const h = await apiGetJson<{ operations: OperationRow[]; nextCursor: string | null }>(
        `/api/admin/ops/backups?resource=history&cursor=${encodeURIComponent(nextCursor)}`,
      );
      setOperations((prev) => [...prev, ...(h.operations ?? [])]);
      setNextCursor(h.nextCursor);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo cargar más historial");
    }
  }

  async function handleCreateBackup() {
    const ok = await dialogs.confirm({
      title: "¿Crear un backup ahora?",
      description: "Crea un branch Neon (copy-on-write) con snapshot del estado actual. No afecta la base en producción.",
      confirmLabel: "Crear backup",
    });
    if (!ok) return;
    setBusy("create");
    try {
      await apiSendJson("/api/admin/ops/backups", "POST", { action: "create_backup" });
      toast.success("Backup creado");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear el backup");
    } finally {
      setBusy(null);
    }
  }

  async function handleVerify() {
    setBusy("verify");
    try {
      const r = await apiSendJson<{ ok: boolean; detail: Record<string, unknown> }>(
        "/api/admin/ops/backups",
        "POST",
        { action: "verify_latest" },
      );
      if (r.ok) toast.success("Backup verificado OK");
      else toast.error("Verificación falló — revisá el historial");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo verificar");
    } finally {
      setBusy(null);
    }
  }

  async function handleRestorePlan(branchId: string) {
    const ok = await dialogs.confirm({
      title: "¿Generar plan de restore?",
      description:
        "El restore de datos no está automatizado — esto genera los pasos manuales a seguir, sin modificar ningún dato.",
      confirmLabel: "Generar plan",
    });
    if (!ok) return;
    setBusy(`restore-${branchId}`);
    try {
      const r = await apiSendJson<{ plan: Record<string, unknown> }>("/api/admin/ops/backups", "POST", {
        action: "restore_plan",
        branchId,
      });
      setRestorePlan(r.plan);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo generar el plan");
    } finally {
      setBusy(null);
    }
  }

  async function handleExportTenant() {
    const ref = exportTenant.trim();
    if (!ref) {
      toast.error("Ingresá un tenant ID o slug");
      return;
    }
    setBusy("export");
    try {
      const isId = ref.startsWith("t-");
      const data = await apiSendJson<Record<string, unknown>>("/api/admin/ops/backups", "POST", {
        action: "tenant_export",
        ...(isId ? { tenantId: ref } : { tenantSlug: ref }),
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenant-export-${ref}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export generado");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo exportar el tenant");
    } finally {
      setBusy(null);
    }
  }

  const meta = status ? STATUS_META[status.status] : STATUS_META.unknown;
  const StatusIcon = meta.icon;

  return (
    <div>
      <h1 className="px-6 pt-5 pb-2 text-lg font-bold text-foreground">Backup & Restore</h1>
      <div className="space-y-6 p-4 sm:p-6">
        <OpsSubnav hint="Salvavidas operativo del SaaS: estado real de backups, verificación y plan de restore." />

        {loading && !status ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando estado…
          </div>
        ) : status ? (
          <>
            <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${meta.className}`}>
              <div className="flex items-center gap-2">
                <StatusIcon className="h-5 w-5" />
                <div>
                  <p className="font-semibold">{meta.label}</p>
                  {status.error ? (
                    <p className="text-xs opacity-80">{status.error}</p>
                  ) : (
                    <p className="text-xs opacity-80">
                      Último backup: {status.lastBackupAt ? `${fmtDate(status.lastBackupAt)} (${ageLabel(status.lastBackupAgeHours)})` : "ninguno"}
                    </p>
                  )}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Actualizar
              </Button>
            </div>

            {status.configured && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: "Backups totales", value: status.totalBackups },
                  { label: "Verificados", value: status.verifiedCount },
                  { label: "Operaciones fallidas", value: status.failedCount },
                  { label: "R2 configurado", value: status.r2Configured ? "Sí" : "No" },
                ].map((k) => (
                  <div key={k.label} className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">{k.label}</p>
                    <p className="text-xl font-semibold tabular-nums">{k.value}</p>
                  </div>
                ))}
              </div>
            )}

            {status.configured && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => void handleCreateBackup()} disabled={busy != null}>
                  {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Crear backup
                </Button>
                <Button size="sm" variant="outline" onClick={() => void handleVerify()} disabled={busy != null || status.totalBackups === 0}>
                  {busy === "verify" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Verificar último backup
                </Button>
              </div>
            )}

            {status.configured && (
              <section>
                <h2 className="mb-2 text-sm font-medium">Backups recientes ({branches.length})</h2>
                {branches.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground">Sin backups todavía.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="p-3 text-left font-medium">Fecha</th>
                          <th className="p-3 text-left font-medium">Branch</th>
                          <th className="p-3 text-left font-medium">Estado</th>
                          <th className="p-3 text-left font-medium">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {branches.slice(0, 10).map((b) => (
                          <tr key={b.id} className="border-t hover:bg-muted/20">
                            <td className="p-3 text-xs">{fmtDate(b.created_at)}</td>
                            <td className="p-3 font-mono text-xs">{b.name}</td>
                            <td className="p-3 text-xs">{b.current_state ?? "—"}</td>
                            <td className="p-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => void handleRestorePlan(b.id)}
                                disabled={busy != null}
                              >
                                {busy === `restore-${b.id}` ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                                Generar plan de restore
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            <section className="space-y-2 rounded-lg border p-4">
              <h2 className="text-sm font-medium">Exportar datos de un tenant</h2>
              <p className="text-xs text-muted-foreground">
                Descarga un JSON con catálogo, usuarios, config y suscripción del tenant (sin secrets ni órdenes).
              </p>
              <div className="flex flex-wrap gap-2">
                <Input
                  placeholder="tenant id (t-…) o slug"
                  value={exportTenant}
                  onChange={(e) => setExportTenant(e.target.value)}
                  className="max-w-xs"
                />
                <Button size="sm" variant="outline" onClick={() => void handleExportTenant()} disabled={busy != null}>
                  {busy === "export" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Exportar
                </Button>
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-sm font-medium">Historial de operaciones ({operations.length})</h2>
              {operations.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">Sin operaciones registradas todavía.</p>
              ) : (
                <div className="space-y-1">
                  {operations.map((op) => (
                    <div key={op.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className={op.ok === false ? "text-red-600" : op.ok === true ? "text-green-600" : "text-muted-foreground"}>
                          {op.ok === false ? "✗" : op.ok === true ? "✓" : "•"}
                        </span>
                        <span className="font-medium">{op.type}</span>
                        <span className="text-muted-foreground">{op.actor ?? "—"}</span>
                      </div>
                      <span className="text-muted-foreground">{fmtDate(op.createdAt)}</span>
                    </div>
                  ))}
                  {nextCursor && (
                    <Button size="sm" variant="ghost" onClick={() => void loadMoreHistory()}>
                      Cargar más
                    </Button>
                  )}
                </div>
              )}
            </section>
          </>
        ) : null}

        <div className="rounded-lg border bg-muted/20 p-4">
          <button
            className="flex w-full items-center justify-between text-left text-sm font-medium"
            onClick={() => setShowTechnical((v) => !v)}
          >
            Detalle técnico / scripts avanzados
            <ChevronDown className={`h-4 w-4 transition-transform ${showTechnical ? "rotate-180" : ""}`} />
          </button>
          {showTechnical && (
            <div className="mt-3 grid grid-cols-1 gap-2 text-xs font-mono sm:grid-cols-2">
              {[
                { label: "Crear backup DB (CLI)", cmd: "node scripts/backup/create-db-backup.mjs" },
                { label: "Verificar backup (CLI)", cmd: "node scripts/backup/verify-db-backup.mjs --latest" },
                { label: "Restore — plan (CLI)", cmd: "node scripts/backup/restore-db-backup.mjs --confirm <manifest.json>" },
                { label: "R2 manifest", cmd: "node scripts/backup/backup-r2-manifest.mjs" },
                { label: "Verificar R2", cmd: "node scripts/backup/verify-r2-assets.mjs" },
                { label: "Export tenant (CLI)", cmd: "node scripts/backup/tenant-export.mjs --tenant-id <id>" },
              ].map((s) => (
                <div key={s.cmd} className="flex flex-col gap-0.5">
                  <span className="font-sans text-[11px] text-muted-foreground">{s.label}</span>
                  <code className="break-all rounded bg-background px-2 py-0.5 text-[11px] text-foreground">{s.cmd}</code>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={restorePlan != null} onOpenChange={(v) => !v && setRestorePlan(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Plan de restore</DialogTitle>
          </DialogHeader>
          {restorePlan ? (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">{String(restorePlan.note ?? "")}</p>
              <ol className="list-decimal space-y-1.5 pl-4">
                {(restorePlan.steps as Array<{ step: number; description: string; automated: boolean }>).map((s) => (
                  <li key={s.step}>
                    {s.description}
                    {!s.automated && <span className="ml-1 text-xs text-amber-600">(manual)</span>}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={() => setRestorePlan(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
